import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export type CategoryType = 'continents' | 'countries' | 'ports'

export interface PortInfoNavigationState {
  expandedCategories: CategoryType[]
  activeCategory: CategoryType | null
  selectedPortName: string | null
  selectedTerminal: string | null
  selectedBerthName: string | null
  selectedContinentCode: string | null
  selectedCountryCode: string | null
  selectedEntryId: string | null
}

export interface PortInfoNavigationHandlers {
  handleSelectCategory: (category: CategoryType) => void
  handleSelectPort: (portName: string) => void
  handleSelectTerminal: (terminalName: string) => void
  handleSelectBerth: (berthName: string) => void
  handleSelectContinent: (code: string) => void
  handleSelectCountry: (code: string) => void
  handleSelectEntry: (id: string, category: 'continents' | 'countries') => void
}

export function usePortInfoNavigation(): PortInfoNavigationState &
  PortInfoNavigationHandlers {
  const [searchParams, setSearchParams] = useSearchParams()

  const expandedCategories = searchParams.getAll('category') as CategoryType[]
  const activeCategory = searchParams.get(
    'activeCategory',
  ) as CategoryType | null
  const selectedPortName = searchParams.get('port')
  const selectedTerminal = searchParams.get('terminal')
  const selectedBerthName = searchParams.get('berth')
  const selectedContinentCode = searchParams.get('continentCode')
  const selectedCountryCode = searchParams.get('countryCode')
  const selectedEntryId = searchParams.get('entryId')

  const handleSelectCategory = useCallback(
    (category: CategoryType) => {
      setSearchParams((prev) => {
        const currentCategories = prev.getAll('category')
        const isExpanded = currentCategories.includes(category)
        const isActive = prev.get('activeCategory') === category

        prev.delete('category')

        if (isExpanded && isActive) {
          currentCategories
            .filter((c) => c !== category)
            .forEach((c) => {
              prev.append('category', c)
            })
          if (category === 'ports') {
            prev.delete('port')
            prev.delete('terminal')
            prev.delete('berth')
          } else if (category === 'continents') {
            prev.delete('continentCode')
            prev.delete('entryId')
          } else if (category === 'countries') {
            prev.delete('countryCode')
            prev.delete('entryId')
          }
          prev.delete('activeCategory')
        } else if (isExpanded && !isActive) {
          currentCategories.forEach((c) => {
            prev.append('category', c)
          })
          prev.set('activeCategory', category)
        } else {
          ;[...currentCategories, category].forEach((c) => {
            prev.append('category', c)
          })
          prev.set('activeCategory', category)
        }
        return prev
      })
    },
    [setSearchParams],
  )

  const handleSelectPort = useCallback(
    (portName: string) => {
      setSearchParams((prev) => {
        prev.delete('entryId')
        prev.set('activeCategory', 'ports')
        if (prev.get('port') === portName) {
          prev.delete('port')
          prev.delete('terminal')
          prev.delete('berth')
        } else {
          prev.set('port', portName)
          prev.delete('terminal')
          prev.delete('berth')
        }
        return prev
      })
    },
    [setSearchParams],
  )

  const handleSelectTerminal = useCallback(
    (terminalName: string) => {
      setSearchParams((prev) => {
        prev.delete('entryId')
        prev.set('activeCategory', 'ports')
        prev.set('terminal', terminalName)
        prev.delete('berth')
        return prev
      })
    },
    [setSearchParams],
  )

  const handleSelectBerth = useCallback(
    (berthName: string) => {
      setSearchParams((prev) => {
        if (berthName) {
          prev.set('berth', berthName)
        } else {
          prev.delete('berth')
        }
        return prev
      })
    },
    [setSearchParams],
  )

  const handleSelectContinent = useCallback(
    (code: string) => {
      setSearchParams((prev) => {
        prev.delete('terminal')
        prev.set('activeCategory', 'continents')
        if (prev.get('continentCode') === code) {
          prev.delete('continentCode')
          prev.delete('entryId')
        } else {
          prev.set('continentCode', code)
          prev.delete('entryId')
        }
        return prev
      })
    },
    [setSearchParams],
  )

  const handleSelectCountry = useCallback(
    (code: string) => {
      setSearchParams((prev) => {
        prev.delete('terminal')
        prev.set('activeCategory', 'countries')
        if (prev.get('countryCode') === code) {
          prev.delete('countryCode')
          prev.delete('entryId')
        } else {
          prev.set('countryCode', code)
          prev.delete('entryId')
        }
        return prev
      })
    },
    [setSearchParams],
  )

  const handleSelectEntry = useCallback(
    (id: string, category: 'continents' | 'countries') => {
      setSearchParams((prev) => {
        prev.delete('terminal')
        prev.set('activeCategory', category)
        if (prev.get('entryId') === id) {
          prev.delete('entryId')
        } else {
          prev.set('entryId', id)
        }
        return prev
      })
    },
    [setSearchParams],
  )

  return {
    expandedCategories,
    activeCategory,
    selectedPortName,
    selectedTerminal,
    selectedBerthName,
    selectedContinentCode,
    selectedCountryCode,
    selectedEntryId,
    handleSelectCategory,
    handleSelectPort,
    handleSelectTerminal,
    handleSelectBerth,
    handleSelectContinent,
    handleSelectCountry,
    handleSelectEntry,
  }
}
